const COMBINED_STATUS = {
  published: { label: 'Published', tone: 'green' },
  scheduled: { label: 'Scheduled', tone: 'yellow' },
  draft: { label: 'Draft', tone: 'gray' },
  processing: { label: 'Processing', tone: 'blue' },
  deleting: { label: 'Deleting', tone: 'blue' },
  partially_published: { label: 'Partially published', tone: 'amber' },
  needs_attention: { label: 'Needs attention', tone: 'red' }
};

const DESTINATIONS = {
  youtube: {
    name: 'YouTube',
    icon: 'youtube',
    states: {
      draft: 'Draft',
      scheduled: 'Scheduled',
      processing: 'Processing',
      drafting: 'Processing',
      deleting: 'Deleting',
      published: 'Published'
    }
  },
  apple_video: {
    name: 'Apple Podcasts',
    icon: 'apple',
    states: {
      draft: 'Draft',
      pending: 'Draft',
      scheduled: 'Scheduled',
      processing: 'Processing',
      deleting: 'Deleting',
      published: 'Published',
      rejected: 'Rejected',
      failed: 'Failed',
      archived: 'Archived',
      pending_with_issue: 'Pending with issue',
      unavailable: 'Unavailable',
      blocked: 'Blocked',
      unknown: 'Unknown'
    }
  }
};

const AUDIO_STATES = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  processing: 'Processing',
  drafting: 'Processing',
  deleting: 'Processing',
  published: 'Published'
};

/**
 * Maps the API's combined video state to its customer-facing badge.
 *
 * @param {string} state combined state from the episode-list payload.
 * @returns {{label: string, tone: string}} badge presentation.
 */
function combinedVideoStatusPresentation(state) {
  return COMBINED_STATUS[state] || COMBINED_STATUS.needs_attention;
}

/**
 * Builds ordered, customer-facing hover details for selected destinations.
 *
 * @param {{destinations?: Object<string, string>}|null} videoStatus video status payload.
 * @returns {Array<{key: string, name: string, status: string, icon: string, tooltip: string}>}
 */
function destinationStatusDetails(videoStatus) {
  const states = videoStatus?.destinations || {};

  return Object.keys(DESTINATIONS).filter((key) => states[key]).map((key) => {
    const destination = DESTINATIONS[key];
    const status = destination.states[states[key]] || 'Needs attention';

    return {
      key,
      name: destination.name,
      status,
      icon: destination.icon,
      tooltip: `${destination.name}: ${status}`
    };
  });
}

/**
 * Builds the platform icons shown beneath the episode title.
 *
 * @param {Object|null} episode episode-list payload row.
 * @returns {Array<{key: string, name: string, status: string, icon: string, tooltip: string}>}
 */
function episodePlatformDetails(episode) {
  if (!episode) return [];

  const platforms = destinationStatusDetails(episode.video_status);
  const hasAudio = ['audio_only', 'audio_and_video'].includes(episode.episode_type);
  if (!hasAudio) return platforms;

  const audioState = episode.audio_episode_data?.episode_stage || episode.episode_stage;
  const status = AUDIO_STATES[audioState] || 'Needs attention';

  return platforms.concat({
    key: 'audio',
    name: 'Audio',
    status,
    icon: 'audio',
    tooltip: `Audio: ${status}`
  });
}

/**
 * Builds the episode-library list presentation for one episode.
 *
 * @param {{video_status?: Object}|null} episode episode-list payload row.
 * @returns {{badge: {label: string, tone: string}|null, platforms: Array<Object>}}
 */
function episodeLibraryStatusPresentation(episode) {
  const videoStatus = episode?.video_status;
  let badge = videoStatus
    ? combinedVideoStatusPresentation(videoStatus.state)
    : null;
  const destinationIsDeleting = Object.values(videoStatus?.destinations || {}).includes('deleting');

  if (badge && videoStatus.state === 'processing' && destinationIsDeleting) {
    badge = { ...badge, label: 'Deleting' };
  }

  return {
    badge,
    platforms: episodePlatformDetails(episode)
  };
}

if (typeof window !== 'undefined') {
  window.episodeLibraryStatus = {
    presentation: episodeLibraryStatusPresentation
  };
}

module.exports = {
  combinedVideoStatusPresentation,
  destinationStatusDetails,
  episodePlatformDetails,
  episodeLibraryStatusPresentation
};
